
import React from 'react';

export const EquityAndFinancing: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Understanding Equity and Financing Terms
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Equity and financing concepts are fundamental to apartment building transactions. Understanding these terms 
            helps you make informed decisions about leverage, returns, and deal structure.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Equity Fundamentals</h3>
              
              <div className="space-y-6">
                <div className="bg-teal-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-teal-800 mb-3">What Is Equity?</h4>
                  <p className="text-gray-700 mb-4">
                    Equity is the difference between your property's current market value and any outstanding debt. 
                    It represents your true ownership stake and accumulated wealth in the property.
                  </p>
                  <div className="bg-white p-4 rounded-lg border border-teal-200">
                    <div className="text-center text-lg font-bold text-teal-800 mb-2">
                      Equity = Market Value - Outstanding Debt
                    </div>
                    <div className="text-sm text-gray-600 text-center">
                      Example: $2,000,000 value - $1,200,000 mortgage = $800,000 equity
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Building Equity Over Time</h4>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm"><strong>Principal Paydown:</strong> Monthly mortgage payments reduce debt</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm"><strong>Appreciation:</strong> Property value increases over time</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm"><strong>Improvements:</strong> Capital investments increase value</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-gray-700 text-sm"><strong>NOI Growth:</strong> Higher income drives higher valuations</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h5 className="font-semibold text-green-800 mb-2">Equity Benefits</h5>
                  <p className="text-green-700 text-sm">
                    Equity provides financial flexibility through refinancing options, serves as collateral for additional 
                    investments, and represents accumulated wealth that can be realized through sale or exchange.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/debdfb037fd0b9fa65a14f494b20090d.jpg"
              alt="Apartment building equity growth and wealth accumulation"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-lg mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Key Financing Terms</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-money-dollar-circle-line text-teal-600 mr-3"></i>
                Principal
              </h4>
              <p className="text-gray-600 text-sm mb-3">
                The original loan amount borrowed to purchase the property. Principal is paid down over time through 
                monthly mortgage payments, building equity.
              </p>
              <div className="text-xs text-teal-600 font-medium">
                Key Point: Lower principal = higher equity
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-percent-line text-blue-600 mr-3"></i>
                Interest Rate
              </h4>
              <p className="text-gray-600 text-sm mb-3">
                The cost of borrowing money, expressed as an annual percentage. Lower rates reduce monthly payments 
                and total borrowing costs over the loan term.
              </p>
              <div className="text-xs text-blue-600 font-medium">
                Impact: 1% rate change = ~10% payment difference
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-shield-line text-green-600 mr-3"></i>
                Loan-to-Value (LTV)
              </h4>
              <p className="text-gray-600 text-sm mb-3">
                The ratio of the loan amount to the property's appraised value. Lower LTV ratios indicate more equity 
                and less financial risk for lenders.
              </p>
              <div className="text-xs text-green-600 font-medium">
                Formula: Loan Amount ÷ Property Value
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-calendar-line text-purple-600 mr-3"></i>
                Amortization
              </h4>
              <p className="text-gray-600 text-sm mb-3">
                The process of paying down the loan principal over time through scheduled payments. Longer amortization 
                periods mean lower monthly payments but more total interest.
              </p>
              <div className="text-xs text-purple-600 font-medium">
                Common Terms: 15, 20, 25, 30 years
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-line-chart-line text-red-600 mr-3"></i>
                DSCR
              </h4>
              <p className="text-gray-600 text-sm mb-3">
                Debt Service Coverage Ratio measures the property's ability to service its debt. Calculated by dividing 
                NOI by annual debt service payments.
              </p>
              <div className="text-xs text-red-600 font-medium">
                Typical Requirement: 1.20x or higher
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                <i className="ri-lock-line text-yellow-600 mr-3"></i>
                Prepayment Penalty
              </h4>
              <p className="text-gray-600 text-sm mb-3">
                Fees charged for paying off the loan early. Common in commercial loans to protect lenders from 
                interest rate risk and early payoff.
              </p>
              <div className="text-xs text-yellow-600 font-medium">
                Types: Fixed penalty, yield maintenance, defeasance
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=commercial%20real%20estate%20financing%20options%20comparison%20chart%2C%20apartment%20building%20loan%20types%20and%20terms%20analysis%2C%20mortgage%20interest%20rates%20and%20amortization%20schedules%20visualization&width=600&height=400&seq=financing-options-comparison&orientation=landscape"
              alt="Commercial real estate financing options and loan comparison"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Common Financing Structures</h3>
              
              <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Traditional Bank Loans</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Typical LTV:</span>
                      <span className="font-medium">70-80%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Amortization:</span>
                      <span className="font-medium">20-30 years</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">DSCR Required:</span>
                      <span className="font-medium">1.20-1.35x</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Rate Type:</span>
                      <span className="font-medium">Fixed or Variable</span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-800 mb-3">CMBS Loans</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Typical LTV:</span>
                      <span className="font-medium">75-80%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Loan Amount:</span>
                      <span className="font-medium">$2M+ minimum</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Term:</span>
                      <span className="font-medium">7-10 years</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Prepayment:</span>
                      <span className="font-medium">Yield maintenance</span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-yellow-800 mb-3">Agency Loans (Fannie/Freddie)</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Typical LTV:</span>
                      <span className="font-medium">75-80%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Min Units:</span>
                      <span className="font-medium">5+ units</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Amortization:</span>
                      <span className="font-medium">25-30 years</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Benefits:</span>
                      <span className="font-medium">Lower rates, non-recourse</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Leverage Amplifies Returns and Risks</h3>
            <p className="text-lg opacity-90 leading-relaxed mb-4">
              Understanding financing terms helps you optimize leverage to maximize returns while managing risk. 
              The right financing structure can significantly impact your cash flow, tax benefits, and overall investment returns.
            </p>
            <div className="text-sm opacity-75">
              Remember: Higher leverage increases both potential returns and potential losses. Choose financing that aligns with your risk tolerance and investment goals.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
